import classNames from 'classnames';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useGetContractor } from 'entities/profile';
import { useUpdateCampaignWizard, isCampaignType } from 'entities/campaign';
import type { CampaignTypeKey } from 'entities/campaign';
import { Button } from 'shared/ui/Button';
import { isImpersonate } from 'shared/lib/api';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';
import { showDefaultErrorToast } from 'shared/lib/notifications';

import {
  useCampaignId,
  useCampaignType,
  useCreateCampaignStoreActions,
  useServiceObject,
} from './CreateCampaignStoreHook';
import { StepTypeAlertBox } from './StepTypeAlertBox';
import { CampaignTypeSelector } from './CampaignTypeSelector';

const INPUT_NAME = 'type';

type StepTypeProps = {
  onBack: () => void;
  onNext: () => void;
};

type FormType = {
  type: CampaignTypeKey;
};

export function StepType(props: StepTypeProps) {
  const service = useServiceObject();

  if (!service) {
    throw new Error('The service object is not available. Make sure it is properly initialized.');
  }

  const { onBack, onNext } = props;
  const campaignType = useCampaignType();
  const campaignId = useCampaignId();
  const { mutateAsync, isLoading, reset } = useUpdateCampaignWizard(campaignId);
  const isSalesCardDisabled = useGetContractor().data?.data.paymentMethod === 'stripe';
  const { updateStepData } = useCreateCampaignStoreActions();
  const methods = useForm<FormType>({
    defaultValues: {
      type: campaignType,
    },
  });

  const { handleSubmit, control } = methods;
  const selectedValue = useWatch({ control, name: INPUT_NAME });
  const isBtnDisabled = !isCampaignType(selectedValue);
  const showSales = isImpersonate();

  const onCardSelected = (value: CampaignTypeKey | undefined) => {
    updateStepData({ preSelectedType: value });
  };

  const onBackBtnClick = () => {
    onCardSelected(undefined);
    onBack();
  };

  const onSubmit = async ({ type }: FormType) => {
    reset();
    updateStepData({ type });

    try {
      await mutateAsync({ payload: { type } });
      onNext();
    } catch (e) {
      showDefaultErrorToast();
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="text-center text-xl font-medium tracking-normal">Choose your campaign type</div>
        <div className="mt-2 text-center text-sm font-normal">
          The type cannot be changed after you create the campaign
        </div>
        <div
          className={classNames('mt-8 grid grid-flow-col gap-x-6', {
            'auto-cols-[minmax(0,_3fr)]': showSales,
          })}
        >
          <CampaignTypeSelector
            isWide={!showSales}
            isDisabled={!service.campaignTypes.includes('lead')}
            inputName={INPUT_NAME}
            value="lead"
            onSelect={onCardSelected}
            {...getDataAutoTestAttributes(['radio-create-wizard-lead'])}
          />
          <CampaignTypeSelector
            isWide={!showSales}
            isDisabled={!service.campaignTypes.includes('appt')}
            inputName={INPUT_NAME}
            value="appt"
            onSelect={onCardSelected}
            {...getDataAutoTestAttributes(['radio-create-wizard-appt'])}
          />
          {showSales && (
            <CampaignTypeSelector
              isWide={false}
              inputName={INPUT_NAME}
              value="sales"
              isDisabled={!service.campaignTypes.includes('sales') || isSalesCardDisabled}
              onSelect={onCardSelected}
              {...getDataAutoTestAttributes(['radio-create-wizard-sales'])}
            />
          )}
        </div>
        <StepTypeAlertBox
          service={service}
          isImpersonate={showSales}
          isSalesCardDisabled={isSalesCardDisabled}
        />
        <div className="mt-8 flex justify-between">
          <Button
            type="button"
            onClick={onBackBtnClick}
            variant="outline-second"
            {...getDataAutoTestAttributes(['button-create-wizard-back'])}
          >
            Back
          </Button>
          <Button
            type="submit"
            disabled={isBtnDisabled}
            loading={isLoading}
            {...getDataAutoTestAttributes(['button-create-wizard-next'])}
          >
            Next
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
